import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import KeyIcon from '@mui/icons-material/Key';
import DataTable from '../../components/common/DataTable';
import OrganizationForm from './OrganizationForm';
import OrgApiKeysDialog from './OrgApiKeysDialog';
import { useOrganizations, useDeleteOrganization, useTestApiKey } from '../../hooks/useApi';
import type { Organization } from '../../types';

export default function Organizations() {
  const [formOpen, setFormOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState<Organization | null>(null);
  const [apiTestResults, setApiTestResults] = useState<Record<string, boolean | null>>({});
  const [apiKeysOrg, setApiKeysOrg] = useState<Organization | null>(null);

  const { data: organizations, isLoading, error, refetch } = useOrganizations();
  const deleteOrg = useDeleteOrganization();
  const testApiKey = useTestApiKey();

  const handleCreate = () => {
    setSelectedOrg(null);
    setFormOpen(true);
  };

  const handleEdit = (org: Organization) => {
    setSelectedOrg(org);
    setFormOpen(true);
  };

  const handleDelete = (org: Organization) => {
    setOrgToDelete(org);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (orgToDelete) {
      await deleteOrg.mutateAsync(orgToDelete.id);
      setDeleteDialogOpen(false);
      setOrgToDelete(null);
    }
  };

  const handleTestApiKey = async (orgId: string) => {
    setApiTestResults(prev => ({ ...prev, [orgId]: null }));
    const result = await testApiKey.mutateAsync(orgId);
    setApiTestResults(prev => ({ ...prev, [orgId]: result.isValid }));
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedOrg(null);
    refetch();
  };

  const columns = [
    {
      id: 'name',
      label: 'Navn',
      minWidth: 200,
    },
    {
      id: 'cvr',
      label: 'CVR',
      minWidth: 100,
    },
    {
      id: 'city',
      label: 'By',
      minWidth: 120,
    },
    {
      id: 'userCount',
      label: 'Brugere',
      align: 'center' as const,
      minWidth: 80,
    },
    {
      id: 'doraApiKey',
      label: 'API Status',
      minWidth: 120,
      sortable: false,
      format: (_: unknown, row: Organization) => {
        const testResult = apiTestResults[row.id];
        if (testResult === null) {
          return <CircularProgress size={16} />;
        }
        if (testResult === true) {
          return <Chip icon={<CheckCircleIcon />} label="OK" size="small" color="success" />;
        }
        if (testResult === false) {
          return <Chip icon={<ErrorIcon />} label="Fejl" size="small" color="error" />;
        }
        return (
          <Button size="small" onClick={() => handleTestApiKey(row.id)}>
            Test
          </Button>
        );
      },
    },
    {
      id: 'actions',
      label: '',
      align: 'right' as const,
      minWidth: 100,
      sortable: false,
      format: (_: unknown, row: Organization) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
          <Tooltip title="API-nøgler (rental-app m.fl.)">
            <IconButton size="small" onClick={() => setApiKeysOrg(row)}>
              <KeyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Rediger">
            <IconButton size="small" onClick={() => handleEdit(row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Slet">
            <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (error) {
    return (
      <Box>
        <Alert severity="error">Kunne ikke hente organisationer.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Organisationer</Typography>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleCreate}>
          Ny
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : organizations ? (
        <DataTable
          columns={columns}
          data={organizations}
          rowKey="id"
          searchPlaceholder="Søg organisation..."
        />
      ) : null}

      {/* Create/Edit Dialog */}
      <Dialog open={formOpen} onClose={handleFormClose} maxWidth="sm" fullWidth>
        <OrganizationForm
          organization={selectedOrg}
          onClose={handleFormClose}
        />
      </Dialog>

      {/* API-nøgler dialog */}
      {apiKeysOrg && (
        <OrgApiKeysDialog organization={apiKeysOrg} onClose={() => setApiKeysOrg(null)} />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Slet organisation?</DialogTitle>
        <DialogContent>
          <Typography>
            Er du sikker på du vil slette <strong>{orgToDelete?.name}</strong>?
            Alle tilknyttede brugere vil miste adgang.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annuller</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Slet
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
